import { motion } from "framer-motion";
import { Check } from "lucide-react";

const tiers = [
  {
    name: "Landing",
    price: "€79",
    tagline: "One page. Maximum impact.",
    features: [
      "Single high-converting page",
      "Mobile optimized",
      "Contact form",
      "Basic SEO setup",
      "5–7 day delivery",
    ],
    featured: false,
  },
  {
    name: "Business",
    price: "€149",
    tagline: "The complete online presence.",
    features: [
      "Up to 5 custom pages",
      "Premium design",
      "SEO-ready structure",
      "Contact & lead forms",
      "Mobile & speed optimized",
      "1 round of revisions",
    ],
    featured: true,
  },
  {
    name: "Redesign+",
    price: "Custom",
    tagline: "Reinvent an existing brand.",
    features: [
      "Full website redesign",
      "Unlimited pages",
      "Advanced interactions",
      "Conversion optimization",
      "Priority support",
    ],
    featured: false,
  },
];

export default function Pricing() {
  return (
    <section
      id="pricing"
      data-testid="pricing-section"
      className="relative py-28 md:py-40 border-t border-white/5"
    >
      <div className="max-w-[1400px] mx-auto px-6 md:px-12">
        <div className="mb-16 max-w-2xl">
          <div className="flex items-center gap-4 mb-6">
            <span className="h-px w-12 bg-[#d4af37]" />
            <span className="text-xs uppercase tracking-[0.3em] gold-text">Investment</span>
          </div>
          <h2 className="font-serif-display font-light text-4xl md:text-6xl tracking-tight">
            Clear, honest <span className="italic gold-text">pricing</span>
          </h2>
          <p className="mt-6 text-white/50 leading-relaxed">
            Transparent packages so you know exactly what to expect. Every project is tailored —
            these are starting points.
          </p>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-3 gap-6 lg:gap-8">
          {tiers.map((t, i) => (
            <motion.div
              key={t.name}
              data-testid={`pricing-card-${i}`}
              initial={{ opacity: 0, y: 40 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true, margin: "-40px" }}
              transition={{ duration: 0.6, delay: i * 0.1 }}
              className={`group relative rounded-3xl p-8 md:p-10 flex flex-col ${
                t.featured
                  ? "border border-[#d4af37]/50 bg-[#d4af37]/[0.04]"
                  : "glass-card"
              }`}
            >
              {t.featured && (
                <span className="absolute top-6 right-6 px-3 py-1 rounded-full bg-[#d4af37] text-black text-[10px] uppercase tracking-[0.2em] font-medium">
                  Popular
                </span>
              )}
              <h3 className="font-serif-display text-3xl mb-2">{t.name}</h3>
              <p className="text-white/50 text-sm mb-8">{t.tagline}</p>
              <div className="mb-8">
                <span className="font-serif-display text-5xl gold-text">{t.price}</span>
                {t.price !== "Custom" && <span className="text-white/40 text-sm ml-2">starting</span>}
              </div>
              <ul className="flex flex-col gap-4 mb-10 flex-1">
                {t.features.map((f) => (
                  <li key={f} className="flex items-start gap-3 text-white/70">
                    <Check size={18} className="text-[#d4af37] shrink-0 mt-0.5" strokeWidth={2} />
                    <span>{f}</span>
                  </li>
                ))}
              </ul>
              <a
                href="#contact"
                data-testid={`pricing-cta-${i}`}
                className={`inline-flex items-center justify-center px-7 py-3.5 rounded-full font-medium tracking-wide transition-colors duration-300 ${
                  t.featured
                    ? "bg-[#d4af37] text-black hover:bg-[#f3e5ab]"
                    : "border border-white/20 text-white hover:border-[#d4af37] hover:text-[#d4af37]"
                }`}
              >
                Get Started
              </a>
            </motion.div>
          ))}
        </div>
      </div>
    </section>
  );
}
